import React, { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";

const Hero = ({ slides = [], intervalMs = 5000 }) => {
  const { pathname } = useLocation();
  // const isHomePage = pathname === "/";
  const [activeSlide, setActiveSlide] = useState(0);
  const hasMultiple = slides.length > 1;

  // ✅ Ensure we always have a valid slide object
  const current = useMemo(() => slides[activeSlide] || {}, [slides, activeSlide]);

  const {
    images,
    title,
    subtitle,
    heading,
    subheading,
    tagline,
    location,
  } = current;

  // ✅ Normalized titles and subtitles
  const heroTitle = title || heading || "";
  const heroSubtitle = subtitle || subheading || "";

  // ✅ Updated to handle S3 image array format
  const imageUrl =
    Array.isArray(images) && images.length > 0 ? images[0] : "";
  const encodedImage = imageUrl ? encodeURI(imageUrl) : "";

  // ✅ Auto slide rotation
  useEffect(() => {
    if (!hasMultiple) return;
    const id = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % slides.length);
    }, intervalMs);
    return () => clearInterval(id);
  }, [slides.length, hasMultiple, intervalMs]);

  return (
    <section
      className="relative w-full h-[60vh] sm:h-[70vh] md:h-[85vh] lg:h-auto lg:aspect-video overflow-hidden flex items-center justify-center text-center"
      aria-label="Hero Section"
    >
      {/* Background Image Layer */}
      <div
        className="absolute inset-0 transition-[background-image] duration-700"
        style={{
          backgroundImage: encodedImage
            ? `linear-gradient(rgba(0,0,0,0.65), rgba(0,0,0,0.65)), url('${encodedImage}')`
            : "linear-gradient(rgba(0,0,0,0.65), rgba(0,0,0,0.65))",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />

      {/* Content Overlay */}
      <div className="relative z-10 text-white px-4 sm:px-6 md:px-8 lg:px-12 max-w-5xl mx-auto">
        {heroTitle && (
          <h1 className="text-base sm:text-lg md:text-2xl lg:text-3xl mb-2 md:mb-3 leading-snug">
            {heroTitle}
          </h1>
        )}
        {heroSubtitle && (
          <p className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold leading-tight mb-3 sm:mb-4 md:mb-6">
            {heroSubtitle}
          </p>
        )}
        {tagline && (
          <p className="text-sm sm:text-base md:text-lg lg:text-xl italic mb-3 sm:mb-4">
            {tagline}
          </p>
        )}
        {location && (
          <p className="text-xs sm:text-sm md:text-base lg:text-lg tracking-wide uppercase mt-2">
            📍 {location}
          </p>
        )}
      </div>

      {/* Indicators */}
      {hasMultiple && (
        <div className="absolute bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-20">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setActiveSlide(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`w-6 sm:w-8 md:w-10 h-1.5 rounded-full transition ${
                i === activeSlide
                  ? "bg-white"
                  : "bg-white/40 hover:bg-white/70"
              }`}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default Hero;